import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Assignment,
  Medication,
  Science,
  Image,
  LocalHospital,
  Note,
  Receipt,
  Undo,
  MonitorHeart,
  Visibility as ViewResultIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import axios from "axios";
import PrescriptionDialog from "../../../../Components/PrescriptionDialog";
import PrescriptionsList from "../../../../Components/PrescriptionsList";
import LabTestDialog from "./Forms/LabTestDialog";
import XRayDialog from "./Forms/XRayDialog";
import EchoDialog from "./Forms/EchoDialog";
import UltrasoundDialog from "./Forms/UltrasoundDialog";
import EndoscopyDialog from "./Forms/EndoscopyDialog";
import CTScanDialog from "./Forms/CTScanDialog";
import MRoyDialog from "./Forms/MRoyDialog";
import EncounterDialog from "./Forms/EncounterDialog";
import ClinicalNoteDialog from "./Forms/ClinicalNoteDialog";
import CostSummary from "./CostSummary";
import ZoomableImageDialog from "../../../../Components/GeneralComponents/ZoomableImageDialog/ZoomableImageDialog";
import ProfileSkeleton from "../../../../Components/GeneralComponents/profileSkeleton/ProfileSkeleton";
const REFUND_STORAGE_KEY = "reception_refunds";

const TabPanel = ({ children, value, index }) => {
  return value === index && <Box sx={{ p: 2 }}>{children}</Box>;
};

const ComprehensiveProfile = ({
  patientId,
  ticketId = null,
  showFullHistory = false,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user?.user;

  // Debug: Log patientId to ensure it's being passed
  React.useEffect(() => {
    if (!patientId) {
      console.warn("ComprehensiveProfile: patientId is missing!", {
        patientId,
        ticketId,
      });
    }
  }, [patientId, ticketId]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [labOrderDetails, setLabOrderDetails] = useState({});
  const [labOrderLoading, setLabOrderLoading] = useState({});
  const [diagnosticDetails, setDiagnosticDetails] = useState({});
  const [diagnosticLoading, setDiagnosticLoading] = useState({});
  const [diagnosticVisible, setDiagnosticVisible] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerSrc, setImageViewerSrc] = useState("");
  const [imageViewerTitle, setImageViewerTitle] = useState("");
  const [xrayDialogOpen, setXrayDialogOpen] = useState(false);
  const [echoDialogOpen, setEchoDialogOpen] = useState(false);
  const [ultrasoundDialogOpen, setUltrasoundDialogOpen] = useState(false);
  const [endoscopyDialogOpen, setEndoscopyDialogOpen] = useState(false);
  const [ctScanDialogOpen, setCTScanDialogOpen] = useState(false);
  const [mRoyDialogOpen, setMRoyDialogOpen] = useState(false);
  const [encounterDialogOpen, setEncounterDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [refundRows, setRefundRows] = useState([]);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketMonth, setTicketMonth] = useState("");
  const [ticketDate, setTicketDate] = useState("");
  const [ticketYear, setTicketYear] = useState("");
  const [selectedTicketKey, setSelectedTicketKey] = useState("");
  const filteredRefundRows = React.useMemo(() => {
    if (!Array.isArray(refundRows)) return [];
    if (ticketId != null) {
      return refundRows.filter(
        (row) => String(row?.ticket_id) === String(ticketId),
      );
    }
    return refundRows;
  }, [refundRows, ticketId]);

  React.useEffect(() => {
    const loadRefunds = () => {
      try {
        const rows = JSON.parse(
          localStorage.getItem(REFUND_STORAGE_KEY) || "[]",
        );
        setRefundRows(Array.isArray(rows) ? rows : []);
      } catch {
        setRefundRows([]);
      }
    };
    loadRefunds();
    window.addEventListener("reception-refunds-updated", loadRefunds);
    window.addEventListener("storage", loadRefunds);
    return () => {
      window.removeEventListener("reception-refunds-updated", loadRefunds);
      window.removeEventListener("storage", loadRefunds);
    };
  }, []);
  // Fetch unified EHR data
  const {
    data: ehrData,
    isLoading,
    refetch,
  } = useFetchObjects(
    patientId ? ["unified-ehr", patientId, ticketId] : null,
    patientId
      ? `patients/${patientId}/unified-ehr/${ticketId ? `?ticket_id=${ticketId}` : ""}`
      : "",
    token,
  );

  // Group data by ticket/encounter
  const groupedData = React.useMemo(() => {
    if (!ehrData?.timeline) return {};

    const groups = {};
    ehrData.timeline.forEach((item) => {
      // Get ticket identifier - try multiple sources
      const ticketKey =
        item.data?.ticket_number ||
        (item.data?.ticket_id ? `TICKET-${item.data.ticket_id}` : null) ||
        "no-ticket";

      // Get encounter identifier
      const encounterKey = item.data?.encounter_id
        ? `ENC-${item.data.encounter_id}`
        : item.type === "encounter"
          ? `ENC-${item.id}`
          : "no-encounter";

      if (!groups[ticketKey]) {
        groups[ticketKey] = {
          ticket_number: item.data?.ticket_number || ticketKey,
          ticket_id: item.data?.ticket_id,
          encounters: {},
        };
      }

      if (!groups[ticketKey].encounters[encounterKey]) {
        groups[ticketKey].encounters[encounterKey] = {
          encounter_id: encounterKey,
          items: [],
        };
      }

      groups[ticketKey].encounters[encounterKey].items.push(item);
    });

    return groups;
  }, [ehrData]);

  const ticketTimeline = React.useMemo(() => {
    return Object.entries(groupedData)
      .map(([ticketKey, group]) => {
        const encounters = Object.entries(group.encounters || {});
        const items = encounters.flatMap(
          ([, encounter]) => encounter.items || [],
        );
        const dates = items
          .map((item) => (item?.date ? new Date(item.date) : null))
          .filter((date) => date && !Number.isNaN(date.getTime()))
          .sort((left, right) => left.getTime() - right.getTime());
        const latestDate = dates[dates.length - 1] || null;
        const earliestDate = dates[0] || null;
        const searchableText = [
          ticketKey,
          group.ticket_number,
          group.ticket_id,
          ...items.flatMap((item) => [
            item?.title,
            item?.subtitle,
            item?.type,
            item?.data?.doctor_display,
            item?.data?.doctor,
            item?.data?.provider_name_display,
            item?.data?.created_by,
            item?.data?.recorded_by,
            item?.data?.notes,
          ]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const typeCounts = items.reduce((accumulator, item) => {
          const key = item?.type || "other";
          accumulator[key] = (accumulator[key] || 0) + 1;
          return accumulator;
        }, {});

        return {
          ticketKey,
          ticketNumber: group.ticket_number || ticketKey,
          ticketId: group.ticket_id,
          encounters,
          items,
          latestDate,
          earliestDate,
          searchableText,
          typeCounts,
        };
      })
      .sort((left, right) => {
        const leftDate = left.latestDate?.getTime() || 0;
        const rightDate = right.latestDate?.getTime() || 0;
        return rightDate - leftDate;
      });
  }, [groupedData]);

  const filteredTicketTimeline = React.useMemo(() => {
    return ticketTimeline.filter((ticket) => {
      if (ticketSearch.trim()) {
        const query = ticketSearch.trim().toLowerCase();
        if (!ticket.searchableText.includes(query)) return false;
      }

      if (ticketYear.trim()) {
        const yearMatch = ticket.items.some((item) => {
          const itemDate = item?.date ? new Date(item.date) : null;
          return (
            itemDate &&
            !Number.isNaN(itemDate.getTime()) &&
            String(itemDate.getFullYear()) === ticketYear.trim()
          );
        });
        if (!yearMatch) return false;
      }

      if (ticketMonth) {
        const [year, month] = ticketMonth.split("-");
        const monthMatch = ticket.items.some((item) => {
          const itemDate = item?.date ? new Date(item.date) : null;
          return (
            itemDate &&
            !Number.isNaN(itemDate.getTime()) &&
            String(itemDate.getFullYear()) === year &&
            String(itemDate.getMonth() + 1).padStart(2, "0") === month
          );
        });
        if (!monthMatch) return false;
      }

      if (ticketDate) {
        const exactMatch = ticket.items.some((item) => {
          const itemDate = item?.date ? new Date(item.date) : null;
          return (
            itemDate &&
            !Number.isNaN(itemDate.getTime()) &&
            itemDate.toLocaleDateString("en-CA") === ticketDate
          );
        });
        if (!exactMatch) return false;
      }

      return true;
    });
  }, [ticketTimeline, ticketSearch, ticketYear, ticketMonth, ticketDate]);

  const selectedTicket = React.useMemo(() => {
    if (!selectedTicketKey) return null;
    return (
      filteredTicketTimeline.find(
        (ticket) => ticket.ticketKey === selectedTicketKey,
      ) || null
    );
  }, [filteredTicketTimeline, selectedTicketKey]);

  React.useEffect(() => {
    if (
      selectedTicketKey &&
      !filteredTicketTimeline.some(
        (ticket) => ticket.ticketKey === selectedTicketKey,
      )
    ) {
      setSelectedTicketKey("");
    }
  }, [filteredTicketTimeline, selectedTicketKey]);

  const activeTicketLabel = React.useMemo(() => {
    if (!selectedTicket) return null;
    return selectedTicket.ticketNumber || null;
  }, [selectedTicket]);

  const getEndoscopyItems = React.useCallback(() => {
    const timeline = ehrData?.timeline || [];
    const direct = timeline.filter(
      (item) => (item.type === "endoscopy" || item.type === "endoscopy_record") && matchesSelectedTicket(item),
    );
    if (direct.length) return direct;

    // Fallback: include timeline items that mention endoscopy in title/subtitle/type
    return timeline.filter((item) => {
      const text =
        `${item?.title || ""} ${item?.subtitle || ""} ${item?.type || ""}`.toLowerCase();
      return text.includes("endoscopy") && matchesSelectedTicket(item);
    });
  }, [ehrData, matchesSelectedTicket]);
  const getCTScanItems = React.useCallback(() => {
    const timeline = ehrData?.timeline || [];
    const direct = timeline.filter(
      (item) => (item.type === "ct_scan_record" || item.type === "ct_scan") && matchesSelectedTicket(item),
    );
    if (direct.length) return direct;
    return timeline.filter((item) => {
      const text =
        `${item?.title || ""} ${item?.subtitle || ""} ${item?.type || ""}`.toLowerCase();
      return text.includes("ct scan") && matchesSelectedTicket(item);
    });
  }, [ehrData, matchesSelectedTicket]);
  const getMRoyItems = React.useCallback(() => {
    const timeline = ehrData?.timeline || [];
    const direct = timeline.filter(
      (item) => (item.type === "m_roy_record" || item.type === "m_roy") && matchesSelectedTicket(item),
    );
    if (direct.length) return direct;
    return timeline.filter((item) => {
      const text =
        `${item?.title || ""} ${item?.subtitle || ""} ${item?.type || ""}`.toLowerCase();
      return (
        (text.includes("mri") || text.includes("m-roy") || text.includes("m roy")) &&
        matchesSelectedTicket(item)
      );
    });
  }, [ehrData, matchesSelectedTicket]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAddPrescription = () => {
    setPrescriptionDialogOpen(true);
  };

  const handlePrescriptionAdded = () => {
    setPrescriptionDialogOpen(false);
    refetch();
  };

  const handlePrescriptionDialogClose = () => {
    setPrescriptionDialogOpen(false);
    refetch();
  };

  const configuredApiBase = process.env.REACT_APP_API_URL || "";
  const apiBase = configuredApiBase.replace(/\/+$/, "");
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsoluteMediaUrl = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };
  const formatProfileDateTime = (item) => {
    if (!item?.date) return "-";
    const dateText = new Date(item.date).toLocaleDateString();
    const rawTime = item?.data?.record_time;
    if (rawTime) {
      const base = String(rawTime).slice(0, 5);
      const [hStr, mStr] = base.split(":");
      const h = Number(hStr);
      const m = Number(mStr);
      if (Number.isFinite(h) && Number.isFinite(m)) {
        const period = h >= 12 ? "PM" : "AM";
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${dateText}, ${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
      }
      return `${dateText}, ${base}`;
    }
    return new Date(item.date).toLocaleString();
  };

  const getTicketNumberFromItem = (item, fallback = {}) => {
    const ticketNumber =
      item?.data?.ticket_number ||
      item?.data?.ticket_display ||
      item?.data?.ticket_number_display ||
      item?.ticket_number ||
      fallback.ticket_number ||
      (item?.data?.ticket_id ? `TKT-${item.data.ticket_id}` : null) ||
      (item?.data?.ticket?.ticket_number
        ? item.data.ticket.ticket_number
        : null) ||
      (item?.data?.ticket?.id ? `TKT-${item.data.ticket.id}` : null) ||
      (fallback.ticket_id ? `TKT-${fallback.ticket_id}` : null);

    if (!ticketNumber) return null;

    return {
      ticketNumber: String(ticketNumber),
      ticketId:
        item?.data?.ticket_id ||
        item?.ticket_id ||
        fallback.ticket_id ||
        item?.data?.ticket ||
        null,
    };
  };

  const renderTicketChip = (item, fallback = {}) => {
    const ticketInfo = getTicketNumberFromItem(item, fallback);
    if (!ticketInfo) return null;

    return (
      <Chip
        label={`Ticket: ${ticketInfo.ticketNumber}`}
        size="small"
        variant="outlined"
        color="default"
      />
    );
  };

  function matchesSelectedTicket(item, fallback = {}) {
    if (!selectedTicket) return true;
    const itemTicket = getTicketNumberFromItem(item, fallback);
    if (!itemTicket) return false;

    if (
      selectedTicket.ticketId != null &&
      itemTicket.ticketId != null &&
      String(itemTicket.ticketId) === String(selectedTicket.ticketId)
    ) {
      return true;
    }

    if (
      selectedTicket.ticketNumber &&
      itemTicket.ticketNumber &&
      String(itemTicket.ticketNumber).toLowerCase() ===
        String(selectedTicket.ticketNumber).toLowerCase()
    ) {
      return true;
    }

    if (
      selectedTicket.ticketKey === "no-ticket" &&
      (!itemTicket.ticketId || itemTicket.ticketNumber === "no-ticket")
    ) {
      return true;
    }

    return false;
  }

  const renderTimelineItemCard = (item, ticket) => (
    <Card
      key={`${ticket.ticketKey}-${item.id}`}
      variant="outlined"
      sx={{ mb: 1 }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            alignItems: "start",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {item.title || item.type || "Item"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {formatProfileDateTime(item)}
            </Typography>
            <Box
              sx={{ mt: 0.75, display: "flex", gap: 0.75, flexWrap: "wrap" }}
            >
              {renderTicketChip(item, {
                ticket_number: ticket.ticketNumber,
                ticket_id: ticket.ticketId,
              })}
              <Chip
                label={(item.type || "other").replace(/_/g, " ")}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            {item.subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {item.subtitle}
              </Typography>
            )}
            {(item.data?.created_by ||
              item.data?.doctor ||
              item.data?.recorded_by ||
              item.data?.nurse ||
              item.data?.provider_name_display) && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {t("profile.addedBy") || t("profile.createdBy") || "Added by"}:{" "}
                {item.data.created_by ||
                  item.data.doctor ||
                  item.data.recorded_by ||
                  item.data.nurse ||
                  item.data.provider_name_display}
              </Typography>
            )}
            {(item.data?.notes ||
              item.data?.test_result ||
              item.data?.findings ||
              item.data?.diagnosis) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {item.data?.notes && <span>{item.data.notes}</span>}
                {item.data?.test_result && (
                  <span>{item.data?.test_result}</span>
                )}
                {item.data?.findings && <span>{item.data.findings}</span>}
                {item.data?.diagnosis && <span>{item.data.diagnosis}</span>}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSelectedTicketDetail = (ticket) => {
    if (!ticket) return null;

    const encounterEntries = ticket.encounters || [];
    const selectedDateLabel = ticket.latestDate
      ? ticket.latestDate.toLocaleString()
      : ticket.earliestDate
        ? ticket.earliestDate.toLocaleString()
        : "-";

    return (
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("profile.ticket") || "Ticket"}: {ticket.ticketNumber}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Chip label={`${ticket.items.length} items`} size="small" />
              <Chip
                label={`${encounterEntries.length} encounters`}
                size="small"
                variant="outlined"
              />
              <Chip label={selectedDateLabel} size="small" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t("profile.tip") ||
                "Click a ticket on the left to review all its encounters and records."}
            </Typography>
          </CardContent>
        </Card>

        {encounterEntries.length === 0 ? (
          <Alert severity="info">
            {t("profile.noData") || "No data available"}
          </Alert>
        ) : (
          encounterEntries.map(([encounterKey, encounter]) => {
            const encounterMeta =
              encounter.items?.find((item) => item.type === "encounter" && matchesSelectedTicket(item)) ||
              encounter.items?.[0];
            const nestedItems = (encounter.items || []).filter(
              (item) => item.type !== "encounter",
            );
            return (
              <Card
                key={`${ticket.ticketKey}-${encounterKey}`}
                variant="outlined"
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {encounterMeta?.title ||
                      `${t("profile.encounter") || "Encounter"} ${encounterKey.replace("ENC-", "")}`}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(encounterMeta || nestedItems[0] || {}, {
                      ticket_number: ticket.ticketNumber,
                      ticket_id: ticket.ticketId,
                    })}
                    <Chip
                      label={`${nestedItems.length} records`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {encounterMeta?.date
                      ? new Date(encounterMeta.date).toLocaleString()
                      : "-"}
                  </Typography>
                  {encounterMeta?.data?.chief_complaint && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>
                        {t("profile.chiefComplaint") || "Chief Complaint"}:
                      </strong>{" "}
                      {encounterMeta.data.chief_complaint}
                    </Typography>
                  )}
                  {encounterMeta?.data?.diagnosis && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.diagnosis") || "Diagnosis"}:</strong>{" "}
                      {encounterMeta.data.diagnosis}
                    </Typography>
                  )}
                  {encounterMeta?.data?.notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.notes", "Notes")}:</strong>{" "}
                      {encounterMeta.data.notes}
                    </Typography>
                  )}
                  {(encounterMeta?.data?.clinical_notes_count ?? 0) > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      <strong>{t("profile.notes", "Clinical Notes")}:</strong>{" "}
                      {encounterMeta.data.clinical_notes_count}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    {nestedItems.length > 0 ? (
                      nestedItems.map((item) =>
                        renderTimelineItemCard(item, ticket),
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.noData") || "No data available"}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Stack>
    );
  };

  const fetchLabOrderResults = async (orderId) => {
    if (labOrderDetails[orderId] || labOrderLoading[orderId]) return;
    setLabOrderLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      const labUrl = apiBase
        ? `${apiBase}/laboratory-test-orders/${orderId}/`
        : `/laboratory-test-orders/${orderId}/`;
      const res = await axios.get(labUrl, {
        headers: { Authorization: `Token ${token}` },
      });
      setLabOrderDetails((prev) => ({ ...prev, [orderId]: res.data }));
    } catch (err) {
      console.error("Failed to fetch lab order results", err);
    } finally {
      setLabOrderLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getDiagnosticKey = (type, id) => `${type}-${id}`;
  const fetchDiagnosticResult = async (type, id) => {
    const key = getDiagnosticKey(type, id);
    if (diagnosticDetails[key] || diagnosticLoading[key]) return;
    setDiagnosticLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const endpoints = {
        xray: "patient-xray",
        ultrasound: "ultrasounds",
        echo: "patient-echo",
        endoscopy: "endoscopy-records",
        ct_scan: "ct-scan-records",
        m_roy: "m-roy-records",
      };
      const diagnosticUrl = apiBase
        ? `${apiBase}/${endpoints[type]}/${id}/`
        : `/${endpoints[type]}/${id}/`;
      const res = await axios.get(diagnosticUrl, {
        headers: { Authorization: `Token ${token}` },
      });
      setDiagnosticDetails((prev) => ({ ...prev, [key]: res.data }));
      setDiagnosticVisible((prev) => ({ ...prev, [key]: true }));
    } catch (err) {
      console.error("Failed to fetch diagnostic result", err);
    } finally {
      setDiagnosticLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  const tabs = [
    { label: t("profile.overview") || "Overview", icon: <Assignment /> },
    {
      label: t("profile.consultations") || "Consultations",
      icon: <LocalHospital />,
    },
    { label: t("profile.notes") || "Clinical Notes", icon: <Note /> },
    {
      label: t("profile.prescriptions") || "Prescriptions",
      icon: <Medication />,
    },
    { label: t("profile.labTests") || "Lab Tests", icon: <Science /> },
    { label: t("profile.xray") || "X-Ray", icon: <Image /> },
    { label: t("profile.ultrasound") || "Ultrasound", icon: <Image /> },
    { label: t("profile.echo") || "Echo", icon: <Image /> },
    { label: t("profile.endoscopy", "Endoscopy"), icon: <Image /> },
    { label: t("profile.ctScan", "CT Scan"), icon: <Image /> },
    { label: t("profile.mri", "MRI"), icon: <Image /> },
    { label: t("profile.admissions") || "Admissions", icon: <LocalHospital /> },
    { label: t("profile.nursing") || "Nursing", icon: <MonitorHeart /> },
    { label: t("reception.refund", "Refund"), icon: <Undo /> },
    { label: t("profile.costs") || "Costs", icon: <Receipt /> },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {showFullHistory
            ? t("profile.fullPatientHistory") || "Full Patient History"
            : ticketId
              ? t("profile.ticketInformation") || "Ticket Information"
              : t("profile.patientProfile") || "Patient Profile"}
        </Typography>
        <Chip
          label={
            activeTicketLabel
              ? `${t("profile.ticket") || "Ticket"}: ${activeTicketLabel}`
              : t("profile.allTickets") || "All Tickets"
          }
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
        />
        {/* Add buttons for each tab */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {selectedTab === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEncounterDialogOpen(true)}
              size="small"
            >
              {t("profile.addEncounter") || "Add Consultation"}
            </Button>
          )}
          {selectedTab === 2 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNoteDialogOpen(true)}
              size="small"
            >
              {t("profile.addNote") || "Add Note"}
            </Button>
          )}
          {selectedTab === 3 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPrescription}
              size="small"
            >
              {t("profile.addPrescription") || "Add Prescription"}
            </Button>
          )}
          {selectedTab === 4 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLabDialogOpen(true)}
              size="small"
            >
              {t("profile.addLabTest") || "Add Lab Test"}
            </Button>
          )}
          {selectedTab === 5 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setXrayDialogOpen(true)}
              size="small"
            >
              {t("profile.addXRay") || "Add X-Ray"}
            </Button>
          )}
          {selectedTab === 6 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUltrasoundDialogOpen(true)}
              size="small"
            >
              {t("profile.addUltrasound") || "Add Ultrasound"}
            </Button>
          )}
          {selectedTab === 7 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEchoDialogOpen(true)}
              size="small"
            >
              {t("profile.addEcho") || "Add Echo"}
            </Button>
          )}
          {selectedTab === 8 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEndoscopyDialogOpen(true)}
              size="small"
            >
              {t("profile.addEndoscopy", "Add Endoscopy")}
            </Button>
          )}
          {selectedTab === 9 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCTScanDialogOpen(true)}
              size="small"
            >
              {t("profile.addCTScan", "Add CT Scan")}
            </Button>
          )}
          {selectedTab === 10 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setMRoyDialogOpen(true)}
              size="small"
            >
              {t("profile.addMRI", "Add MRI")}
            </Button>
          )}
        </Box>
      </Box>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("profile.search") || "Search"}
                  value={ticketSearch}
                  onChange={(event) => setTicketSearch(event.target.value)}
                  placeholder={t("profile.searchTickets") || "Ticket, doctor, note, test..."}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("profile.date") || "Date"}
                  value={ticketDate}
                  onChange={(event) => setTicketDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="month"
                  label={t("profile.month") || "Month"}
                  value={ticketMonth}
                  onChange={(event) => setTicketMonth(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t("profile.year") || "Year"}
                  value={ticketYear}
                  onChange={(event) => setTicketYear(event.target.value)}
                  inputProps={{ min: 1900, max: 2100 }}
                />
                <Button
                  variant="text"
                  onClick={() => {
                    setTicketSearch("");
                    setTicketMonth("");
                    setTicketDate("");
                    setTicketYear("");
                  }}
                >
                  {t("common.clear", "Clear")}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ maxHeight: 240, overflowY: "auto", p: 1 }}>
              {filteredTicketTimeline.length === 0 ? (
                <Alert severity="info">
                  {t("profile.noData") || "No data available"}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  <Card
                    variant="outlined"
                    onClick={() => setSelectedTicketKey("")}
                    sx={{
                      cursor: "pointer",
                      borderColor: !selectedTicketKey ? "primary.main" : "divider",
                      bgcolor: !selectedTicketKey ? "action.selected" : "background.paper",
                    }}
                  >
                    <CardContent sx={{ py: 1.25 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {t("profile.allTickets") || "All Tickets"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("profile.showAllTicketsHint") || "Show all patient history"}
                          </Typography>
                        </Box>
                        <Chip label={`${filteredTicketTimeline.length}`} size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                  {filteredTicketTimeline.map((ticket) => {
                      const isSelected = ticket.ticketKey === selectedTicket?.ticketKey;
                      return (
                        <Card
                          key={ticket.ticketKey}
                          variant="outlined"
                          onClick={() => setSelectedTicketKey(ticket.ticketKey)}
                          sx={{
                            cursor: "pointer",
                            borderColor: isSelected ? "primary.main" : "divider",
                            bgcolor: isSelected ? "action.selected" : "background.paper",
                          }}
                        >
                          <CardContent sx={{ py: 1.25 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {ticket.ticketNumber !== "no-ticket"
                                    ? `${t("profile.visitTicket") || "Visit Ticket"}: ${ticket.ticketNumber}`
                                    : t("profile.noTicket") || "No Ticket"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ticket.latestDate ? ticket.latestDate.toLocaleString() : "-"}
                                </Typography>
                              </Box>
                              <Chip label={`${ticket.items.length}`} size="small" />
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            icon={tab.icon}
            label={tab.label}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* Overview Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Typography variant="h6" gutterBottom>
          {t("profile.timeline") || "Timeline"}
        </Typography>
        {selectedTicket ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("profile.visitTicket") || "Visit Ticket"}: {selectedTicket.ticketNumber}
              </Typography>
              {ehrData?.patient_mrn && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("profile.mrn") || "MRN"}: {ehrData.patient_mrn}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("profile.timelineSelectedHint") ||
                  "Use the selector above to choose a ticket, then review the related information in the tabs below."}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("profile.allTickets") || "All Tickets"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("profile.allTicketsHint") ||
                  "No ticket is selected, so the tabs below show the patient’s full history across every ticket."}
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Consultations Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Typography variant="h6" gutterBottom>
          {t("profile.consultations") || "Consultations"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "encounter" && matchesSelectedTicket(item))
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.title}
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    mb: 0.5,
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  {renderTicketChip(item)}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                </Typography>
                {item.data?.chief_complaint && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>
                      {t("profile.chiefComplaint") || "Chief Complaint"}:
                    </strong>{" "}
                    {item.data.chief_complaint}
                  </Typography>
                )}
                {item.data?.diagnosis && (
                  <Typography variant="body2">
                    <strong>{t("profile.diagnosis") || "Diagnosis"}:</strong>{" "}
                    {item.data.diagnosis}
                  </Typography>
                )}
                {(item.data?.created_by_username ||
                  item.data?.provider_name_display) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {t("profile.addedBy") ||
                      t("profile.createdBy") ||
                      "Added by"}
                    :{" "}
                    {item.data.created_by ||
                      item.data.created_by_username ||
                      item.data.provider_name_display}
                    {item.data.created_by_username &&
                      ` (${item.data.created_by_username})`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Clinical Notes Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Typography variant="h6" gutterBottom>
          {t("profile.notes") || "Clinical Notes"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "encounter" && matchesSelectedTicket(item))
          .flatMap(
            (encounter) =>
              encounter.data?.clinical_notes?.map((note) => ({
                ...note,
                encounter_date: encounter.date,
                ticket_number:
                  encounter.data?.ticket_number ||
                  encounter.data?.ticket_display ||
                  encounter.data?.ticket_number_display ||
                  encounter.data?.ticket?.ticket_number ||
                  encounter.data?.ticket_id ||
                  encounter.data?.ticket,
                ticket_id:
                  encounter.data?.ticket_id ||
                  encounter.data?.ticket?.id ||
                  encounter.ticket_id ||
                  null,
              })) || [],
          )
          .map((note, idx) => (
            <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {note.note_type_display} -{" "}
                  {new Date(note.created_at).toLocaleString()}
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    mb: 0.5,
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  {renderTicketChip(note, {
                    ticket_number: note.ticket_number,
                    ticket_id: note.ticket_id,
                  })}
                </Box>
                {note.subjective && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>S:</strong> {note.subjective}
                  </Typography>
                )}
                {note.objective && (
                  <Typography variant="body2">
                    <strong>O:</strong> {note.objective}
                  </Typography>
                )}
                {note.assessment && (
                  <Typography variant="body2">
                    <strong>A:</strong> {note.assessment}
                  </Typography>
                )}
                {note.plan && (
                  <Typography variant="body2">
                    <strong>P:</strong> {note.plan}
                  </Typography>
                )}
                {note.content && !note.subjective && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {note.content}
                  </Typography>
                )}
                {(note.created_by || note.created_by_username) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {t("profile.addedBy") ||
                      t("profile.createdBy") ||
                      "Added by"}
                    : {note.created_by}
                    {note.created_by_username &&
                      ` (${note.created_by_username})`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Prescriptions Tab */}
      <TabPanel value={selectedTab} index={3}>
        <PrescriptionsList
          patientId={patientId}
          ticketId={selectedTicket?.ticketId ?? ticketId}
          doctor={user}
        />
      </TabPanel>

      {/* Lab Tests Tab */}
      <TabPanel value={selectedTab} index={4}>
        <Typography variant="h6" gutterBottom>
          {t("profile.labTests") || "Lab Tests"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "laboratory" && matchesSelectedTicket(item))
          .map((item) => {
            const orderId = item.id;
            const orderDetail = labOrderDetails[orderId];
            const loadingResult = labOrderLoading[orderId];
            const testsDisplay = orderDetail?.tests_display || [];
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.result_status_display && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={item.data.result_status_display}
                        size="small"
                        color={
                          item.data.result_status === "given"
                            ? "success"
                            : item.data.result_status === "in_progress"
                              ? "info"
                              : item.data.result_status === "cancelled"
                                ? "default"
                                : "warning"
                        }
                        variant="outlined"
                      />
                    </Box>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!orderDetail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loadingResult ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchLabOrderResults(orderId)}
                      disabled={loadingResult}
                      sx={{ mt: 2 }}
                    >
                      {loadingResult
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ mb: 1 }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                              <TableCell>
                                <strong>{t("profile.tests", "Test(s)")}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t("profile.result", "Result")}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>
                                  {t("profile.normalRange", "Normal range")}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t("profile.units", "Units")}</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {testsDisplay.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  align="center"
                                  color="text.secondary"
                                >
                                  {t(
                                    "profile.noResults",
                                    "No tests in this order.",
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : (
                              testsDisplay.map((test) => {
                                const tt = test.test_type_display || {};
                                const hasResult =
                                  tt.result_value != null &&
                                  tt.result_value !== "";
                                const isParent =
                                  !test.parent_test &&
                                  test.child_tests?.length > 0;
                                const resultDisplay = hasResult
                                  ? tt.result_value
                                  : isParent
                                    ? "—"
                                    : t("profile.pending", "Pending") ||
                                      "Pending";
                                const normalRange =
                                  tt.reference_range ||
                                  (tt.min_value != null && tt.max_value != null
                                    ? `${tt.min_value} - ${tt.max_value}`
                                    : null) ||
                                  "—";
                                return (
                                  <TableRow key={test.id}>
                                    <TableCell
                                      sx={
                                        isParent
                                          ? { fontWeight: "bold" }
                                          : undefined
                                      }
                                    >
                                      {tt.name || "—"}
                                    </TableCell>
                                    <TableCell>{resultDisplay}</TableCell>
                                    <TableCell>{normalRange}</TableCell>
                                    <TableCell>{tt.units || "—"}</TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* X-Ray Tab */}
      <TabPanel value={selectedTab} index={5}>
        <Typography variant="h6" gutterBottom>
          {t("profile.xray") || "X-Ray"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "xray" && matchesSelectedTicket(item))
          .map((item) => {
            const key = getDiagnosticKey("xray", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.xray_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.xray_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("xray", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image),
                            );
                            setImageViewerTitle(
                              t("profile.xray", "X-Ray") +
                                (item.data?.xray_type
                                  ? ` - ${item.data.xray_type}`
                                  : ""),
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="X-Ray"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Ultrasound Tab */}
      <TabPanel value={selectedTab} index={6}>
        <Typography variant="h6" gutterBottom>
          {t("profile.ultrasound") || "Ultrasound"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "ultrasound" && matchesSelectedTicket(item))
          .map((item) => {
            const key = getDiagnosticKey("ultrasound", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.ultrasound_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.ultrasound_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() =>
                        fetchDiagnosticResult("ultrasound", item.id)
                      }
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image),
                            );
                            setImageViewerTitle(
                              t("profile.ultrasound", "Ultrasound") +
                                (item.data?.ultrasound_type
                                  ? ` - ${item.data.ultrasound_type}`
                                  : ""),
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="Ultrasound"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Echo Tab */}
      <TabPanel value={selectedTab} index={7}>
        <Typography variant="h6" gutterBottom>
          {t("profile.echo") || "Echo"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "echo" && matchesSelectedTicket(item))
          .map((item) => {
            const key = getDiagnosticKey("echo", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.echo_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.echo_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("echo", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image),
                            );
                            setImageViewerTitle(
                              t("profile.echo", "Echo") +
                                (item.data?.echo_type
                                  ? ` - ${item.data.echo_type}`
                                  : ""),
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="Echo"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Admissions Tab */}
      <TabPanel value={selectedTab} index={8}>
        <Typography variant="h6" gutterBottom>
          {t("profile.endoscopy", "Endoscopy")}
        </Typography>
        {(() => {
          const endoscopyItems = getEndoscopyItems();

          if (!endoscopyItems.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t("profile.noData") || "No data available"}
              </Typography>
            );
          }

          return endoscopyItems.map((item) => {
            const key = getDiagnosticKey("endoscopy", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const isVisible = diagnosticVisible[key];
            const reportImage = detail?.report_image || item.data?.report_image;
            const testPendings = detail?.test_result || item.data?.test_result;
            const resultNotes = detail?.notes || item.data?.notes;
            const resultAmount = detail?.amount ?? item.data?.amount;
            const resultDiscount = detail?.discount ?? item.data?.discount;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title || t("profile.endoscopy", "Endoscopy")}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatProfileDateTime(item)}
                  </Typography>
                  {item.data?.test_name && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.test_name}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Status:</strong> {item.data?.status || "-"}
                  </Typography>
                  {(item.data?.created_by ||
                    item.data?.created_by_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || "-"}
                      {item.data.created_by_username &&
                        ` (${item.data.created_by_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() =>
                        fetchDiagnosticResult("endoscopy", item.id)
                      }
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading...")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewResultIcon />}
                      onClick={() =>
                        setDiagnosticVisible((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                      sx={{ mt: 2 }}
                    >
                      {isVisible
                        ? t("profile.hideResult", "Hide result")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  )}
                  {detail && isVisible && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {testPendings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Test Pendings:</strong> {testPendings}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Amount:</strong>{" "}
                        {resultAmount != null && resultAmount !== ""
                          ? resultAmount
                          : "-"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Discount:</strong>{" "}
                        {resultDiscount != null && resultDiscount !== ""
                          ? resultDiscount
                          : "-"}
                      </Typography>
                      {resultNotes && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.notes", "Notes")}:</strong>{" "}
                          {resultNotes}
                        </Typography>
                      )}
                      {reportImage && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(toAbsoluteMediaUrl(reportImage));
                            setImageViewerTitle(
                              "Endoscopy" +
                                (item.data?.test_name
                                  ? ` - ${item.data.test_name}`
                                  : ""),
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(reportImage)}
                            alt="Endoscopy"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!testPendings && !resultNotes && !reportImage && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          });
        })()}
      </TabPanel>

      <TabPanel value={selectedTab} index={9}>
        <Typography variant="h6" gutterBottom>
          {t("profile.ctScan", "CT Scan")}
        </Typography>
        {(() => {
          const ctScanItems = getCTScanItems();
          if (!ctScanItems.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t("profile.noData") || "No data available"}
              </Typography>
            );
          }
          return ctScanItems.map((item) => {
            const key = getDiagnosticKey("ct_scan", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const isVisible = diagnosticVisible[key];
            const reportImage = detail?.report_image || item.data?.report_image;
            const testPendings = detail?.test_result || item.data?.test_result;
            const resultNotes = detail?.notes || item.data?.notes;
            return (
              <Card key={`ct-${item.id}`} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title || t("profile.ctScan", "CT Scan")}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatProfileDateTime(item)}
                  </Typography>
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("ct_scan", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading...")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewResultIcon />}
                      onClick={() =>
                        setDiagnosticVisible((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                      sx={{ mt: 2 }}
                    >
                      {isVisible
                        ? t("profile.hideResult", "Hide result")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  )}
                  {detail && isVisible && (
                    <Box sx={{ mt: 2 }}>
                      {testPendings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Test Pendings:</strong> {testPendings}
                        </Typography>
                      )}
                      {resultNotes && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.notes", "Notes")}:</strong>{" "}
                          {resultNotes}
                        </Typography>
                      )}
                      {reportImage && (
                        <img
                          src={toAbsoluteMediaUrl(reportImage)}
                          alt="CT Scan"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 300,
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          });
        })()}
      </TabPanel>

      <TabPanel value={selectedTab} index={10}>
        <Typography variant="h6" gutterBottom>
          {t("profile.mri", "MRI")}
        </Typography>
        {(() => {
          const mRoyItems = getMRoyItems();
          if (!mRoyItems.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t("profile.noData") || "No data available"}
              </Typography>
            );
          }
          return mRoyItems.map((item) => {
            const key = getDiagnosticKey("m_roy", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const isVisible = diagnosticVisible[key];
            const reportImage = detail?.report_image || item.data?.report_image;
            const testPendings = detail?.test_result || item.data?.test_result;
            const resultNotes = detail?.notes || item.data?.notes;
            return (
              <Card key={`mroy-${item.id}`} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title || t("profile.mri", "MRI")}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      mb: 0.5,
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {renderTicketChip(item)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatProfileDateTime(item)}
                  </Typography>
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("m_roy", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading...")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewResultIcon />}
                      onClick={() =>
                        setDiagnosticVisible((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                      sx={{ mt: 2 }}
                    >
                      {isVisible
                        ? t("profile.hideResult", "Hide result")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  )}
                  {detail && isVisible && (
                    <Box sx={{ mt: 2 }}>
                      {testPendings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Test Pendings:</strong> {testPendings}
                        </Typography>
                      )}
                      {resultNotes && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.notes", "Notes")}:</strong>{" "}
                          {resultNotes}
                        </Typography>
                      )}
                      {reportImage && (
                        <img
                          src={toAbsoluteMediaUrl(reportImage)}
                          alt="MRI"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 300,
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          });
        })()}
      </TabPanel>

      <TabPanel value={selectedTab} index={11}>
        <Typography variant="h6" gutterBottom>
          {t("profile.admissions") || "Admissions"}
        </Typography>
        {ehrData?.timeline
          ?.filter(
            (item) =>
              item.type === "ipd_admission" || item.type === "icu_admission",
          )
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                </Typography>
                {item.data?.ward && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{t("profile.ward") || "Ward"}:</strong>{" "}
                    {item.data.ward}
                  </Typography>
                )}
                {item.data?.bed && (
                  <Typography variant="body2">
                    <strong>{t("profile.bed") || "Bed"}:</strong>{" "}
                    {item.data.bed}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Nursing Tab - Vital Signs & nursing data */}
      <TabPanel value={selectedTab} index={12}>
        <Typography variant="h6" gutterBottom>
          {t("profile.nursing") || "Nursing"} –{" "}
          {t("profile.vitalSigns") || "Vital Signs"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "vital_signs")
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                  {item.data?.recorded_by &&
                    ` · ${t("profile.recordedBy") || "Recorded by"}: ${item.data.recorded_by}`}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {item.data?.temperature != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.temperature") || "Temp"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.temperature} °C
                      </Typography>
                    </Grid>
                  )}
                  {(item.data?.blood_pressure_systolic != null ||
                    item.data?.blood_pressure_diastolic != null) && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.bloodPressure") || "BP"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.blood_pressure_systolic ?? "—"}/
                        {item.data.blood_pressure_diastolic ?? "—"} mmHg
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.heart_rate != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.heartRate") || "HR"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.heart_rate} bpm
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.respiratory_rate != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.respiratoryRate") || "RR"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.respiratory_rate} /min
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.oxygen_saturation != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.spO2") || "SpO2"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.oxygen_saturation}%
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.weight != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.weight") || "Weight"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.weight} kg
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {item.data?.notes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {item.data.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Refunds Tab */}
      <TabPanel value={selectedTab} index={13}>
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("reception.refundsHistory", "Refunds History")}
          </Typography>
          {filteredRefundRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("reception.noRefunds", "No refunds found.")}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {filteredRefundRows.map((row) => (
                <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {row.item_label || t("reception.refund", "Refund")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString()
                      : "-"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("reception.refund", "Refund")}:</strong>{" "}
                    {`${Number(row.amount || 0).toFixed(2)} AFN`}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    startIcon={<ViewResultIcon />}
                    onClick={() => setSelectedRefund(row)}
                  >
                    {t("profile.viewResult", "View result")}
                  </Button>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </TabPanel>

      {/* Costs Tab */}
      <TabPanel value={selectedTab} index={14}>
        <CostSummary patientId={patientId} />
      </TabPanel>

      {/* All Dialogs */}
      {prescriptionDialogOpen && (
        <PrescriptionDialog
          open={prescriptionDialogOpen}
          onClose={handlePrescriptionDialogClose}
          patient={patientId != null ? { id: patientId } : undefined}
          initialPatientId={patientId}
          doctor={user}
          ticketId={selectedTicket?.ticketId ?? ticketId}
        />
      )}

      <LabTestDialog
        open={labDialogOpen}
        onClose={() => {
          setLabDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <XRayDialog
        open={xrayDialogOpen}
        onClose={() => {
          setXrayDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <EchoDialog
        open={echoDialogOpen}
        onClose={() => {
          setEchoDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <UltrasoundDialog
        open={ultrasoundDialogOpen}
        onClose={() => {
          setUltrasoundDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <EndoscopyDialog
        open={endoscopyDialogOpen}
        onClose={() => {
          setEndoscopyDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />
      <CTScanDialog
        open={ctScanDialogOpen}
        onClose={() => {
          setCTScanDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />
      <MRoyDialog
        open={mRoyDialogOpen}
        onClose={() => {
          setMRoyDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <EncounterDialog
        open={encounterDialogOpen}
        onClose={() => {
          setEncounterDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <ClinicalNoteDialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={selectedTicket?.ticketId ?? ticketId}
      />

      <ZoomableImageDialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageSrc={imageViewerSrc}
        title={imageViewerTitle}
      />

      <Dialog
        open={!!selectedRefund}
        onClose={() => setSelectedRefund(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("reception.refund", "Refund")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2">
              <strong>{t("reception.item", "Item")}:</strong>{" "}
              {selectedRefund?.item_label || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("reception.refund", "Refund")}:</strong>{" "}
              {`${Number(selectedRefund?.amount || 0).toFixed(2)} AFN`}
            </Typography>
            <Typography variant="body2">
              <strong>{t("reception.note", "Note")}:</strong>{" "}
              {selectedRefund?.note || "-"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRefund(null)}>
            {t("reception.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComprehensiveProfile;
